/**
 * Frontend Legal Publication & Registration Acceptance Configuration
 *
 * Interim Free-Trial Release Configuration:
 * - Terms of Service: terms-interim-2026-11-v1 (from M-interim-terms-of-service.pdf, founder decisions 1-5 approved)
 * - Privacy Policy: privacy-interim-2026-11-v1 (from N-interim-privacy-policy.pdf Variant A, founder decisions 1-5 approved)
 * - Effective date is resolved at publication/release time (never back-dated to when code was written).
 * - Each version identifier maps to a single immutable document text in PUBLISHED_LEGAL_VERSION_HISTORY.
 */

export type LegalContentBlock =
    | { type: 'paragraph'; text: string }
    | { type: 'list'; ordered?: boolean; items: string[] }
    | { type: 'table'; headers: string[]; rows: string[][] };

export interface LegalDocumentSection {
    id: string;
    heading: string;
    paragraphs: string[];
    blocks?: LegalContentBlock[];
}

export interface LegalDocumentMetadata {
    slug: '/privacy' | '/terms';
    title: string;
    shortTitle: string;
    draftSourceReference: string;
    version: string | null;
    approvalStatus: 'pending_counsel_approval' | 'approved';
    effectiveDate: string | null;
    lastUpdated: string | null;
    preambleParagraphs?: string[];
    missingSourceMaterial: string[];
    sections: LegalDocumentSection[];
}

export interface LegalGateOverrides {
    legalDocsPublished?: boolean;
    legalAcceptanceRequired?: boolean;
    publishedTermsVersion?: string | null;
    publishedPrivacyVersion?: string | null;
    effectiveDate?: string | null;
    privacyDocument?: Partial<LegalDocumentMetadata>;
    termsDocument?: Partial<LegalDocumentMetadata>;
}

interface RuntimeLegalWindowConfig {
    legalDocsPublished?: boolean;
    legalAcceptanceRequired?: boolean;
    publishedTermsVersion?: string;
    publishedPrivacyVersion?: string;
    effectiveDate?: string;
    publishedAtUtc?: string;
}

function getRuntimeWindowLegalConfig(): RuntimeLegalWindowConfig | null {
    if (typeof window === 'undefined') return null;
    const raw = (window as any).__BAYWHIZ_LEGAL_CONFIG__;
    if (!raw || typeof raw !== 'object') return null;
    return raw as RuntimeLegalWindowConfig;
}

/**
 * Formats a Date in UTC as "D Month YYYY" (e.g., "9 November 2026") per Q §3.
 */
export function formatLegalPublicationDateUtc(date: Date = new Date()): string {
    return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(date);
}

/**
 * Resolves the publication effective date at runtime so the effective date reflects
 * the actual authorized release date rather than the date the code was written.
 */
export function resolvePublicationEffectiveDate(): string {
    if (testOverrides?.effectiveDate !== undefined && testOverrides.effectiveDate !== null) {
        return testOverrides.effectiveDate;
    }
    const winCfg = getRuntimeWindowLegalConfig();
    if (winCfg?.effectiveDate && winCfg.effectiveDate.trim()) {
        return winCfg.effectiveDate.trim();
    }
    if (winCfg?.publishedAtUtc) {
        const parsed = new Date(winCfg.publishedAtUtc);
        if (!Number.isNaN(parsed.getTime())) {
            return formatLegalPublicationDateUtc(parsed);
        }
    }
    const viteEnvDate =
        typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_LEGAL_EFFECTIVE_DATE
            ? String((import.meta as any).env.VITE_LEGAL_EFFECTIVE_DATE).trim()
            : '';
    if (viteEnvDate) {
        return viteEnvDate;
    }
    return formatLegalPublicationDateUtc(new Date());
}

/**
 * Explicit release gate for /privacy and /terms.
 * Active for the approved interim free-trial release candidate.
 */
export const LEGAL_DOCS_PUBLISHED = true;

/**
 * Explicit registration acceptance gate for Warehouse and Carrier signup forms.
 * Active for the approved interim free-trial release candidate.
 */
export const LEGAL_ACCEPTANCE_REQUIRED = true;

/**
 * Published interim policy version identifiers (Q §2).
 */
export const PUBLISHED_TERMS_VERSION = 'terms-interim-2026-11-v1';
export const PUBLISHED_PRIVACY_VERSION = 'privacy-interim-2026-11-v1';

function blocksToParagraphs(blocks: LegalContentBlock[]): string[] {
    const out: string[] = [];
    for (const b of blocks) {
        if (b.type === 'paragraph') {
            out.push(b.text);
        } else if (b.type === 'list') {
            out.push(...b.items);
        } else if (b.type === 'table') {
            for (const row of b.rows) {
                out.push(row.join(' — '));
            }
        }
    }
    return out;
}

function createSection(id: string, heading: string, blocks: LegalContentBlock[]): LegalDocumentSection {
    return {
        id,
        heading,
        blocks,
        paragraphs: blocksToParagraphs(blocks),
    };
}

const PRIVACY_SECTIONS_V1: LegalDocumentSection[] = [
    createSection('section-1', 'Who we are', [
        {
            type: 'paragraph',
            text: 'BayWhiz is operated by 1284320 B.C. LTD., a corporation incorporated in British Columbia, Canada ("BayWhiz", "we", "us").',
        },
        {
            type: 'list',
            items: [
                'Website: https://baywhiz.com',
                'Privacy and support inquiries: support@baywhiz.com',
                'Person responsible for privacy: our Privacy Officer, reachable at support@baywhiz.com',
            ],
        },
        {
            type: 'paragraph',
            text: 'This Policy explains how we collect, use, disclose and protect personal information in connection with the BayWhiz website and the BayWhiz dock scheduling and warehouse appointment platform (the "Service"). The Service is designed for businesses. It is not intended for personal, family or household use. BayWhiz is currently offered to warehouses through invitation-only free trials and to carriers through free Carrier Portal accounts.',
        },
    ]),
    createSection('section-2', 'Our roles', [
        {
            type: 'paragraph',
            text: 'We handle personal information in different capacities. The capacity that applies determines who you should contact about your information.',
        },
        {
            type: 'paragraph',
            text: 'A. Information we handle for our own business purposes. We decide why and how this information is used. It includes account and login information for warehouse and carrier users, invitation, trial and registration records, business contacts (and billing contacts if a paid subscription is arranged), support communications, demo requests and other website inquiries, security, log and usage records, and our own business communications. This Policy fully governs this information.',
        },
        {
            type: 'paragraph',
            text: 'B. Shared carrier records (Global Carrier Registry and Carrier Portal). Carrier companies and carrier users can have one BayWhiz carrier account and profile that is used to book with more than one warehouse that uses BayWhiz. We maintain these shared carrier accounts and profiles (for example, carrier company name, carrier user names and contact details, and login information) in our Global Carrier Registry. Because several warehouse customers may rely on the same carrier record, these records do not belong to any single warehouse customer. We handle them for our own purposes of operating the Carrier Portal and the Service, and this Policy governs them.',
        },
        {
            type: 'paragraph',
            text: 'C. Information we process on behalf of warehouse customers. Warehouse customers (the businesses that use BayWhiz for their facilities, during a trial or a subscription) use the Service to manage their own operations. Records they create or upload, or that are created for a specific warehouse, are warehouse-specific operational records. Examples are appointments and appointment requests made with that warehouse, purchase orders, receiving and shipping records, dock assignments, check-in and check-out times, notes, and carrier and driver details entered by the warehouse. We process these records to provide the Service to that warehouse customer, under our agreement with them and their instructions, and not for our own unrelated purposes (see Sections 4 and 6). If you have a question or request about information a warehouse holds about you in BayWhiz, please contact that warehouse first. We will help the warehouse respond as required by our agreement and applicable law.',
        },
    ]),
    createSection('section-3', 'Information we collect', [
        {
            type: 'paragraph',
            text: 'Depending on how you use BayWhiz, we may collect:',
        },
        {
            type: 'table',
            headers: ['Category', 'Examples', 'Source'],
            rows: [
                [
                    'Account and contact information',
                    'Name, work email, business phone number, company name, role or job title, facility association',
                    "You, or your organization's administrator",
                ],
                [
                    'Invitation, trial and registration records',
                    "The email address a warehouse invitation was sent to and the invitation's status; trial start and end dates and status; the version of our Terms of Service accepted and of this Policy acknowledged at registration, and the date and time (UTC) of acceptance",
                    'Generated by the Service; BayWhiz',
                ],
                [
                    'Login and authentication information',
                    'Account credentials (passwords are stored only as bcrypt hashes, not in readable form); authentication tokens; records used to revoke tokens (for example, on sign-out)',
                    'You; generated by the Service',
                ],
                [
                    'Shared carrier records',
                    'Carrier company name and profile; carrier user names, contact details and login information in the Global Carrier Registry',
                    'Carrier users; in some cases warehouse users or BayWhiz',
                ],
                [
                    'Warehouse-specific operational records',
                    'Facility and dock information; appointments and appointment requests, including carrier, dispatcher or driver details and load or PO references; check-in and check-out times; receiving records; purchase orders; sales orders; shipping details; notes',
                    'Warehouse customers and their authorized users; carriers when booking with that warehouse',
                ],
                [
                    'Guest-booking information',
                    'Selected details entered when booking through a public booking page',
                    'Carriers or other bookers',
                ],
                [
                    'Activity and audit records',
                    'Records of actions taken in the Service (for example, who approved or changed an appointment, and when)',
                    'Generated by the Service',
                ],
                [
                    'Technical and log information',
                    'IP address, user agent (browser and device information), timestamps, requested pages or endpoints, and error information, recorded in Google Cloud request logs and application logs. Identifiers used for rate limiting are stored in hashed form.',
                    'Generated automatically when you use the website or Service',
                ],
                [
                    'Demo requests and website inquiries',
                    'Information you submit through our demo-request form or other website inquiries, together with the IP address and user agent recorded with the request',
                    'You; generated automatically',
                ],
                [
                    'Support and communications',
                    'Messages you send to support@baywhiz.com and our replies',
                    'You',
                ],
                [
                    'Billing contact information (only if a paid subscription is arranged)',
                    'Billing contact name and email, legal business name and business address, plan and number of facilities, and invoice and payment-status records',
                    'You; any invoicing or payment provider we tell you about before use',
                ],
            ],
        },
        {
            type: 'paragraph',
            text: 'No payment information during the trial. We do not ask for or collect payment card or bank details at registration or during a free trial, and the BayWhiz application does not generate invoices or take payments. Carrier accounts are free. If you later arrange a paid subscription, we will tell you before activation how invoicing and payment work and which provider (if any) processes payment details, and we will update this Policy before that provider is used for personal information. We do not ask you to enter payment card numbers into the BayWhiz application.',
        },
        {
            type: 'paragraph',
            text: 'We ask you not to enter sensitive personal information (for example, health or government ID information) into free-text fields of the Service.',
        },
    ]),
    createSection('section-4', 'Why we use personal information', [
        {
            type: 'paragraph',
            text: 'For our own business purposes (Sections 2A and 2B), we use personal information to:',
        },
        {
            type: 'list',
            ordered: true,
            items: [
                'create and administer warehouse and carrier accounts, issue and validate warehouse invitations, manage free trials, maintain the Global Carrier Registry, and authenticate users;',
                'provide, operate, maintain and support the Service;',
                'send service and transactional messages, such as invitations, account verification, password resets, appointment requests, approvals, changes and confirmations, and trial-status and activation notices;',
                'if you ask for a paid subscription, arrange it, invoice for it, confirm payment, and manually activate the paid plan after payment is confirmed;',
                'secure the Service, prevent misuse (including through rate limiting), investigate incidents and keep audit records;',
                'respond to demo requests, inquiries, support requests and privacy requests, including requests to export or delete data;',
                'keep records of which version of our Terms of Service was accepted, and which version of this Policy was acknowledged, at registration;',
                'we do not use warehouse-specific operational records (Section 2C) for these business purposes. In particular, we do not use them, including in aggregated or de-identified form, for cross-customer analytics, benchmarking, advertising or commercialization (see Section 6);',
                "send marketing or product-news emails, only where we have consent or another basis permitted by Canada's Anti-Spam Legislation. Every such email includes an unsubscribe option;",
                'comply with legal, tax and accounting obligations, and establish or defend legal claims.',
            ],
        },
        {
            type: 'paragraph',
            text: 'For warehouse-specific operational records (Section 2C), we use information only to:',
        },
        {
            type: 'list',
            items: [
                '(a) provide, maintain and secure the Service for the relevant warehouse customer, including the scheduling, analytics, carrier scorecard and recommendation features that the customer uses within its own account;',
                '(b) improve the functionality of the Service for that warehouse customer;',
                '(c) carry out support and operational activities authorized by that customer or needed to operate the Service for it (for example, troubleshooting, security monitoring, audit logging and incident investigation);',
                '(d) comply with applicable legal obligations; and',
                '(e) allow our service providers (Section 8) to process them as necessary to deliver the Service.',
            ],
        },
        {
            type: 'paragraph',
            text: "The features in (a) run on rules, statistical heuristics and deterministic calculations applied to the customer's own data. They do not use generative AI. We do not use customer operational records to train artificial-intelligence or machine-learning models.",
        },
        {
            type: 'paragraph',
            text: 'We will ask for your consent before using personal information for a new purpose, unless the law permits otherwise.',
        },
    ]),
    createSection('section-5', 'Warehouse and carrier accounts', [
        {
            type: 'list',
            items: [
                "Warehouse accounts can be created only through an invitation approved by BayWhiz. Each invitation can be used once, and only with the email address it was sent to. Warehouse accounts are set up by or for the invited warehouse business. That organization's administrators control who has access, and can see and manage records created within their facilities. The Service keeps each warehouse customer's records separate from those of other customers (tenant isolation).",
                'Carrier accounts are free accounts in the Carrier Portal, held in our Global Carrier Registry (Section 2B), that carriers use to request and manage appointments with warehouses that use BayWhiz. When a carrier requests an appointment, the information submitted (for example, carrier and contact details, load or PO references, requested times) is shared with the warehouse receiving the request so that it can review, approve, reassign or decline the request. That appointment information then forms part of the warehouse\'s records (Section 2C).',
                "The warehouse may record operational information about a carrier's visits (for example, arrival, check-in and check-out times, dwell time and receiving exceptions). The warehouse may use this information in features such as carrier reliability scorecards and detention analytics. These are warehouse-specific operational records.",
            ],
        },
    ]),
    createSection('section-6', 'Customer operational records', [
        {
            type: 'paragraph',
            text: 'Warehouse customers are responsible for the operational records they put into BayWhiz. This includes making sure they have authority to provide any personal information about their own staff, carriers, drivers or other third parties.',
        },
        {
            type: 'paragraph',
            text: 'We process these records only for the purposes listed for warehouse-specific operational records in Section 4. We do not:',
        },
        {
            type: 'list',
            items: [
                'sell identifiable warehouse operational records;',
                "use one warehouse customer's identifiable records to benchmark or profile another warehouse customer;",
                "share a warehouse customer's identifiable records with other, unrelated customers;",
                'use these records for advertising or marketing, including our own marketing;',
                'use these records to train artificial-intelligence or machine-learning models; or',
                'use these records, including in aggregated or de-identified form, for cross-customer analytics, benchmarking or commercialization unrelated to providing the Service to that customer.',
            ],
        },
        {
            type: 'paragraph',
            text: "These restrictions do not limit the features a warehouse customer uses within its own account, such as its own scheduling tools, analytics and carrier reliability scorecards. They also do not prevent processing needed to secure the Service, provide authorized support, comply with law, or allow our service providers to deliver the Service, as described in Section 4. Shared carrier records (Section 2B) are not warehouse-specific operational records and are handled as described in Section 2B. Appointment and visit records created with a particular warehouse remain that warehouse's records.",
        },
        {
            type: 'paragraph',
            text: 'Export and deletion of these records are described in Section 11 and in our agreement with the warehouse customer.',
        },
    ]),
    createSection('section-7', 'Browser storage, cookies and analytics', [
        {
            type: 'paragraph',
            text: "Browser storage used by the application. The BayWhiz application stores some information in your browser's local storage and session storage. This includes your authentication token (which keeps you signed in), selected guest-booking information, drafts of forms you are filling in, and interface preferences. BayWhiz does not use cookies for authentication. Information in browser storage remains on your device until it expires, is removed by the application, or you clear it. Anyone with access to your browser profile may be able to use this information, so please sign out and avoid using shared devices or browser profiles. Blocking browser storage may prevent the application from keeping you signed in.",
        },
        {
            type: 'paragraph',
            text: 'Analytics. We do not currently use Google Analytics or any other third-party analytics service on the BayWhiz website or in the BayWhiz application. If we decide to introduce analytics, we will update this Policy before doing so (Section 14).',
        },
    ]),
    createSection('section-8', 'Service providers', [
        {
            type: 'paragraph',
            text: 'We use service providers to help us operate the Service. They may process personal information on our behalf, and only for the purposes of providing their services to us:',
        },
        {
            type: 'table',
            headers: ['Provider', 'Purpose', 'Location of processing'],
            rows: [
                [
                    'Google Cloud Platform',
                    'Application hosting (Cloud Run), application database (Cloud SQL for PostgreSQL), backups and logging',
                    'United States (us-central1 region)',
                ],
                [
                    'Postmark',
                    'Delivery of transactional emails (for example, invitations, password resets and appointment notifications)',
                    'May include the United States or other locations (see Section 9)',
                ],
                [
                    'Microsoft 365 / Exchange Online',
                    'Inbound mailbox hosting for support@baywhiz.com (processing support inquiries, legal notices and privacy-request emails, and our replies)',
                    'May include Canada, the United States or other locations where Microsoft operates (see Section 9)',
                ],
            ],
        },
        {
            type: 'paragraph',
            text: 'We remain responsible for personal information that we transfer to service providers for processing. We use contractual or other means to protect it while it is processed. We may also disclose personal information where required or permitted by law (for example, in response to a valid legal order), to protect the security of the Service, or in connection with a sale or reorganization of our business, subject to applicable law.',
        },
        {
            type: 'paragraph',
            text: 'We do not sell personal information.',
        },
    ]),
    createSection('section-9', 'Storage outside Canada', [
        {
            type: 'paragraph',
            text: "BayWhiz's application hosting, database, backups and logs are currently located in the United States, and some service providers (including our email provider) may process information in the United States or elsewhere. As a result, personal information may be stored or processed outside Canada. While it is outside Canada, it may be accessible to the courts, law enforcement and national security authorities of that jurisdiction under its laws.",
        },
    ]),
    createSection('section-10', 'Security', [
        {
            type: 'paragraph',
            text: 'We use reasonable security arrangements, appropriate to the sensitivity of the information, to protect personal information against unauthorized access, collection, use, disclosure, copying, modification or disposal. These currently include:',
        },
        {
            type: 'list',
            items: [
                'passwords stored only as bcrypt hashes;',
                'validation of authentication tokens (JWTs) on requests, and database-backed token revocation, so that revoked tokens stop working;',
                "tenant isolation, which keeps each warehouse customer's records separate from those of other customers;",
                'operational audit logging of actions taken in the Service;',
                'hashing of identifiers used for rate limiting.',
            ],
        },
        {
            type: 'paragraph',
            text: 'No method of transmission or storage is completely secure, and we cannot guarantee absolute security. Users are responsible for keeping their credentials confidential, for securing their own devices and browsers (which hold their sign-in token, see Section 7), and for telling us promptly at support@baywhiz.com if they suspect unauthorized use of their account.',
        },
        {
            type: 'paragraph',
            text: 'If a privacy breach occurs, we will assess it and give the notifications required by applicable law. We will also notify affected warehouse customers in accordance with our agreement with them.',
        },
    ]),
    createSection('section-11', 'Retention, export and deletion', [
        {
            type: 'paragraph',
            text: 'We keep personal information only as long as needed for the purposes described in this Policy, or as required or permitted by law. Where we use personal information to make a decision that directly affects an individual, we keep it for at least one year after that use, as required by BC law.',
        },
        {
            type: 'paragraph',
            text: 'Current retention practices. The periods below describe how our systems are currently configured. They are not guaranteed deletion deadlines, and they may change as our systems change.',
        },
        {
            type: 'table',
            headers: ['Information', 'Current practice'],
            rows: [
                [
                    'Active account information and operational records',
                    'Kept while the account, trial or subscription is active, and afterwards as described below',
                ],
                [
                    'Invitation, trial and registration-acceptance records',
                    'Kept as long as needed to administer accounts and to show which document versions were accepted or acknowledged. No fixed period is currently set.',
                ],
                [
                    'Database backups',
                    'Our database provider keeps a rolling set of the 7 most recent daily backups plus 7 days of transaction logs for point-in-time recovery. Older backup copies are overwritten on this rolling cycle. Information deleted from the live database may remain in backup copies until they are overwritten.',
                ],
                [
                    'Standard operational logs (including request logs with IP address and user agent)',
                    'Kept in Google Cloud Logging under a standard 30-day retention setting',
                ],
                [
                    'Infrastructure audit logs required by Google Cloud',
                    "Kept for 400 days under Google Cloud's required retention",
                ],
                [
                    'Application audit records (activity history in the Service)',
                    'Kept for security, accountability and dispute-resolution purposes. Some of these records currently have no automatic deletion schedule.',
                ],
                [
                    'Demo-request records',
                    'Kept as long as needed to respond to and follow up on the request, or as required by law. No fixed period is currently set.',
                ],
                [
                    'Transactional email records at Postmark',
                    "Under Postmark's message-history retention for our account",
                ],
                [
                    'Billing and invoice records (only if a paid subscription is arranged)',
                    'As required for tax and accounting purposes',
                ],
                [
                    'Support correspondence',
                    'Kept as long as needed to resolve the request and maintain our support history, or as required by law. No fixed period is currently set.',
                ],
                [
                    'Information in your browser storage',
                    'On your device until it expires, is removed by the application, or you clear it (Section 7)',
                ],
            ],
        },
        {
            type: 'paragraph',
            text: "Requesting an export. While a trial or subscription is active, warehouse users can use the Service's existing appointment history and schedule CSV export. Automated export of a complete account is not currently available. A warehouse customer's account administrator may ask for an export of other available account data by emailing support@baywhiz.com, including after a subscription is cancelled or a trial ends. We prepare such exports manually, in a format we reasonably determine (for example, CSV). Please make any export request promptly, and if possible within 30 days after a cancellation takes effect or a trial ends, because data may later be deleted or de-identified as described below. We will respond within a reasonable time. Export is subject to applicable law, technical feasibility, verification of the requester's authority, and the exceptions below. It covers only that customer's warehouse-specific records, not shared carrier records or other customers' information.",
        },
        {
            type: 'paragraph',
            text: "Requesting deletion. Automated deletion of a complete account is not currently available. Deletion or de-identification is carried out manually by BayWhiz. After a subscription is cancelled or a trial ends without a paid subscription, or on a verified request, we will take reasonable steps to delete or de-identify the customer's warehouse-specific records and associated account information when they are no longer needed, subject to applicable law, technical feasibility and the following exceptions:",
        },
        {
            type: 'list',
            items: [
                'information in backups, which remains until the backup copies are overwritten on their rolling cycle;',
                'information in logs and audit records, which is kept under the practices described above;',
                "shared carrier records (Section 2B) that other warehouse customers rely on. Deleting a warehouse customer's data removes or de-identifies that warehouse's own records, but does not delete shared carrier accounts or profiles that are used with other warehouses;",
                'records we must or may keep for billing, tax, accounting, security or legal purposes, or to establish or defend legal claims; and',
                'information that BC law requires us to keep for at least one year after it was used to make a decision directly affecting an individual.',
            ],
        },
        {
            type: 'paragraph',
            text: "Carrier requests. Carriers and carrier users may ask us directly at support@baywhiz.com to access, correct, export or delete their shared carrier account or profile information. Because warehouse customers may rely on that information for their own records, we may retain or de-identify, rather than delete, information that forms part of a warehouse's appointment history. For information that a particular warehouse holds about a carrier's visits or appointments, carriers should also contact that warehouse (Section 2C).",
        },
    ]),
    createSection('section-12', 'Your rights and choices', [
        {
            type: 'paragraph',
            text: 'Subject to applicable law, you may:',
        },
        {
            type: 'list',
            items: [
                'ask whether we hold personal information about you, and request access to it, together with information about how it has been used and to whom it has been disclosed;',
                'ask us to correct inaccurate or incomplete personal information;',
                'ask us to delete personal information, subject to the limits and exceptions in Section 11;',
                'withdraw consent to a use of your personal information, on reasonable notice. This may limit our ability to provide the Service. For example, an account cannot operate without login information;',
                'unsubscribe from marketing emails at any time using the link in the email.',
            ],
        },
        {
            type: 'paragraph',
            text: 'You may request access to or correction of your personal information by contacting our Privacy Officer at support@baywhiz.com. For this or any other request above, please use the subject "Privacy request". We may need to verify your identity.',
        },
        {
            type: 'paragraph',
            text: 'BayWhiz does not charge fees for routine privacy access or correction requests. We will respond within the time required by applicable law (generally 30 days), subject to permitted extensions.',
        },
        {
            type: 'paragraph',
            text: 'Any exceptional request involving potential fees will be assessed under applicable law, and you will be informed of any permitted fee before it is charged.',
        },
        {
            type: 'paragraph',
            text: 'If your request concerns operational records held by a warehouse customer, we will refer it to that customer or help the customer respond (see Section 2C).',
        },
    ]),
    createSection('section-13', 'Questions and complaints', [
        {
            type: 'paragraph',
            text: 'Please contact our Privacy Officer first at support@baywhiz.com. We will investigate and respond.',
        },
        {
            type: 'paragraph',
            text: 'If you are not satisfied with our response, you may contact:',
        },
        {
            type: 'list',
            items: [
                'Office of the Information and Privacy Commissioner for British Columbia (OIPC BC): https://www.oipc.bc.ca · info@oipc.bc.ca · (250) 387-5629',
                'Office of the Privacy Commissioner of Canada (OPC): https://www.priv.gc.ca/en/report-a-concern/file-a-formal-privacy-complaint/',
            ],
        },
    ]),
    createSection('section-14', 'Changes to this Policy', [
        {
            type: 'paragraph',
            text: "We may update this Policy from time to time. We will post the updated version at https://baywhiz.com/privacy with a new effective date. If the changes are material, we will give at least thirty (30) calendar days' advance notice, for example by email to account administrators or by notice in the Service, before they take effect. Where a change is urgently required to comply with law or to address a security matter, we may give shorter notice, as much as is reasonably practicable. Where applicable law requires your consent to a new use of personal information, we will ask for it (Section 4). Previous versions are available on request.",
        },
    ]),
];

const TERMS_PREAMBLE_V1: string[] = [
    'These Terms of Service (the "Terms") are an agreement between 1284320 B.C. LTD., a corporation incorporated in British Columbia, Canada, operating as BayWhiz ("BayWhiz", "we", "us"), and the business that creates an account or uses the BayWhiz service (the "Customer" or "you"). They govern your use of the BayWhiz website, web application, Carrier Portal and related services (the "Service").',
    'By checking the acceptance box when you register, you agree to these Terms on behalf of the business you represent. If you do not agree, do not register or use the Service.',
    'Current offering. BayWhiz is currently available to warehouses only through invitation-only free trials (Section 5), and to carriers through free Carrier Portal accounts. Paid subscriptions are available only by separate written arrangement with BayWhiz (Section 8). Nothing in these Terms requires a trial or carrier customer to buy a paid subscription.',
];

const TERMS_SECTIONS_V1: LegalDocumentSection[] = [
    createSection('section-1', 'Eligibility and authority', [
        {
            type: 'paragraph',
            text: '1.1 The Service is for business use only. It is not offered to consumers for personal, family or household purposes.',
        },
        {
            type: 'paragraph',
            text: '1.2 The person accepting these Terms confirms that they are at least the age of majority where they live and are authorized to bind the business they represent. "You" then means that business.',
        },
        {
            type: 'paragraph',
            text: '1.3 You must give accurate business information when we ask for it (including your legal business name and, if you arrange a paid subscription, your business address) and keep it up to date.',
        },
    ]),
    createSection('section-2', 'Account types', [
        {
            type: 'paragraph',
            text: '2.1 Warehouse accounts. A warehouse business uses the Service for one or more warehouse facilities, during a free trial (Section 5) or under a paid subscription (Section 8). Its administrators invite and manage its authorized users.',
        },
        {
            type: 'paragraph',
            text: "2.2 Carrier accounts. Carriers may use the Carrier Portal free of charge to request and manage appointments with warehouses that use BayWhiz. Carriers accept these Terms as Customers for their use of the Carrier Portal. The sections on fees and paid subscriptions (Sections 6–9) do not apply to free carrier use. A carrier's relationship with a warehouse, including any rates, detention or accessorial charges, is solely between the carrier and that warehouse. BayWhiz is not a party to it.",
        },
        {
            type: 'paragraph',
            text: '2.3 Shared carrier records. A carrier\'s account and profile are held in BayWhiz\'s Global Carrier Registry and may be used to book with several warehouses that use BayWhiz ("Shared Carrier Records"). BayWhiz maintains Shared Carrier Records to operate the Carrier Portal and the Service. They are not owned or controlled by any single warehouse customer. Carriers may ask BayWhiz at support@baywhiz.com to access, correct, export or delete their Shared Carrier Records, as described in the Privacy Policy. Appointment and visit records created with a particular warehouse are that warehouse\'s Customer Data (Section 11.1).',
        },
        {
            type: 'paragraph',
            text: '2.4 Each user must have their own login. Credentials may not be shared.',
        },
    ]),
    createSection('section-3', 'Your responsibilities and authorized users', [
        {
            type: 'paragraph',
            text: "3.1 You are responsible for your authorized users' activity in your account and for their compliance with these Terms.",
        },
        {
            type: 'paragraph',
            text: '3.2 You must keep credentials confidential and tell us promptly at support@baywhiz.com if you suspect unauthorized access. The Service keeps users signed in by storing an authentication token in browser storage, so you are also responsible for reasonably securing the devices and browsers your users use with the Service (for example, by signing out of shared devices).',
        },
        {
            type: 'paragraph',
            text: '3.3 You are responsible for the accuracy and legality of the data you and your users enter (including purchase orders, appointments and carrier details). You must also have any notices, consents or authority needed to provide personal information about your staff, carriers, drivers or others to BayWhiz.',
        },
        {
            type: 'paragraph',
            text: '3.4 You are responsible for your own operational decisions, including approving, declining or reassigning appointments and dock doors, and receiving goods.',
        },
    ]),
    createSection('section-4', 'Acceptable use', [
        {
            type: 'paragraph',
            text: 'You must not, and must not allow anyone to:',
        },
        {
            type: 'paragraph',
            text: '(a) use the Service in breach of any law, including privacy and anti-spam laws;',
        },
        {
            type: 'paragraph',
            text: "(b) upload malicious code, or attempt to gain unauthorized access to the Service or other customers' data;",
        },
        {
            type: 'paragraph',
            text: '(c) probe, scan, load-test or disrupt the Service, or bypass or attempt to bypass or circumvent rate limits or other security controls, except for testing that BayWhiz has specifically authorized in writing;',
        },
        {
            type: 'paragraph',
            text: '(d) reverse engineer, copy, resell, sublicense or provide the Service to third parties, except authorized users and carriers using the Carrier Portal as intended;',
        },
        {
            type: 'paragraph',
            text: '(e) use the Service to build a competing product;',
        },
        {
            type: 'paragraph',
            text: '(f) enter sensitive personal information (such as health or government ID information) that the Service does not require;',
        },
        {
            type: 'paragraph',
            text: '(g) misrepresent your identity or your authority to book or approve appointments;',
        },
        {
            type: 'paragraph',
            text: '(h) forward, share or transfer a warehouse invitation, or use an invitation that was addressed to someone else.',
        },
    ]),
    createSection('section-5', 'Free trial (invitation only)', [
        {
            type: 'paragraph',
            text: '5.1 Invitation only. A warehouse account can be created only through an invitation approved by BayWhiz. Each invitation can be used once, and only with the email address it was sent to. BayWhiz decides at its discretion whether to issue an invitation.',
        },
        {
            type: 'paragraph',
            text: '5.2 Trial length and features. An approved warehouse receives a free trial of 30 days from trial activation, with access to Operations-plan features (Section 6.2). BayWhiz may extend a trial at its discretion by notice to you.',
        },
        {
            type: 'paragraph',
            text: '5.3 No payment and no obligation to buy. No payment card or other payment information is required or collected for the trial. BayWhiz does not invoice or charge you for the trial, nothing is charged automatically when the trial ends, and the trial does not convert automatically to a paid subscription. You are not obligated to buy a paid subscription.',
        },
        {
            type: 'paragraph',
            text: '5.4 End of trial. When the trial ends, operational access to your warehouse account pauses, unless a paid subscription has been separately agreed and activated under Section 8. To continue, contact support@baywhiz.com. Export and deletion of trial data after a pause are described in Section 11.6.',
        },
        {
            type: 'paragraph',
            text: '5.5 The trial is provided "as is" (Section 17). BayWhiz may change or end trial offers for new trials at any time. Section 10.2 explains how either party may end a trial early.',
        },
    ]),
    createSection('section-6', 'Plans', [
        {
            type: 'paragraph',
            text: '6.1 Starter includes unlimited warehouse users and dock doors; dock scheduling and live dock board; public carrier booking with PO validation; manual appointment approval; dock assignment and reassignment; check-in, check-out and receiving; PO management and CSV import; appointment history and schedule CSV export; and a carrier contact directory.',
        },
        {
            type: 'paragraph',
            text: '6.2 Operations includes unlimited warehouse users and dock doors and everything in Starter, plus: rule-based automated appointment approval; consolidated loads and outbound sales orders; Excel PO import; dwell and detention analytics; carrier reliability scorecards; receiving exception analysis and shortage follow-up POs; historical delay-risk analysis; and rule-based operational recommendations with a deterministic Copilot.',
        },
        {
            type: 'paragraph',
            text: '6.3 We may improve or modify features from time to time. We will not materially reduce the core functionality of your paid plan during a paid period.',
        },
    ]),
    createSection('section-7', 'Pricing for paid subscriptions', [
        {
            type: 'paragraph',
            text: '7.1 Paid subscription prices are in US dollars (USD), per warehouse facility, per month:',
        },
        {
            type: 'list',
            items: [
                'Starter: US$99 per facility per month',
                'Operations: US$199 per facility per month',
            ],
        },
        {
            type: 'paragraph',
            text: 'Both plans include unlimited users and dock doors.',
        },
        {
            type: 'paragraph',
            text: '7.2 Prices exclude applicable taxes. Where BayWhiz is required to charge taxes (for example GST/HST or BC PST), they will be added to the invoice.',
        },
        {
            type: 'paragraph',
            text: "7.3 Price changes. BayWhiz will give you at least thirty (30) calendar days' advance notice of any change to subscription prices, by email to your account administrator or billing contact or through notice in the Service. A price change applies no earlier than the start of your next monthly billing period that begins after the notice period ends.",
        },
    ]),
    createSection('section-8', 'Paid activation and invoicing', [
        {
            type: 'paragraph',
            text: '8.1 Separate activation required. A paid subscription starts only if you and BayWhiz separately agree to it in writing (for example, by an email exchange confirming your plan, number of facilities and billing contact) and BayWhiz then activates it manually under Section 8.4. Registering, using a free trial or accepting these Terms does not by itself create any obligation to pay.',
        },
        {
            type: 'paragraph',
            text: '8.2 To request a paid plan, contact support@baywhiz.com with your plan, number of facilities, legal business name, business address and billing contact.',
        },
        {
            type: 'paragraph',
            text: "8.3 BayWhiz bills manually. The BayWhiz application does not generate invoices, collect payments or issue receipts. Before activation, BayWhiz will confirm in writing how invoices will be issued and paid, including any invoicing or payment provider and the payment methods available. Payments made through a third-party provider are also subject to that provider's terms.",
        },
        {
            type: 'paragraph',
            text: '8.4 Activation only after payment. Your paid subscription is activated manually, and only after BayWhiz confirms that the first invoice has been paid. Some payment methods can take several business days to confirm. We will confirm activation by email.',
        },
        {
            type: 'paragraph',
            text: '8.5 You are responsible for any fees your bank or payment method charges you.',
        },
    ]),
    createSection('section-9', 'Paid subscription term, renewal and payment', [
        {
            type: 'paragraph',
            text: 'This Section applies only to a paid subscription activated under Section 8.',
        },
        {
            type: 'paragraph',
            text: '9.1 Subscriptions are monthly, per facility.',
        },
        {
            type: 'paragraph',
            text: '9.2 Renewal: your subscription continues month to month until it is cancelled under Section 10. BayWhiz invoices each month in advance, before the start of the month it covers.',
        },
        {
            type: 'paragraph',
            text: '9.3 Payment due date: Invoices are due upon receipt. Your first invoice must be paid, and payment confirmed by BayWhiz, before your paid subscription is activated. Renewal invoices are due upon receipt and are subject to the 10-day grace period described in Section 9.4.',
        },
        {
            type: 'paragraph',
            text: '9.4 Late payment — no late fees; 10-calendar-day grace period. BayWhiz does not charge late fees or interest. If a renewal invoice is not paid by its due date, you have a grace period of 10 calendar days after the due date, during which access continues. If the invoice remains unpaid after the 10 calendar days, BayWhiz may manually suspend access under Section 10.4. Suspension is a manual decision by BayWhiz, not an automatic process, and we will try to contact your billing contact before suspending.',
        },
        {
            type: 'paragraph',
            text: '9.5 Refunds: fees are paid monthly in advance. If you cancel, your cancellation takes effect at the end of the paid month (Section 10.1), you keep access until then, and no refund is given for any partial month. Nothing in these Terms limits any refund or other right that applicable law does not allow to be excluded.',
        },
        {
            type: 'paragraph',
            text: '9.6 Taxes: see Section 7.2. You are responsible for any taxes you are required to self-assess.',
        },
        {
            type: 'paragraph',
            text: '9.7 Disputed invoices: If you believe an invoice contains an error, please notify BayWhiz in writing at support@baywhiz.com within thirty (30) calendar days of the invoice date, describing the disputed amount and the reason. We will review the dispute in good faith and work with you toward a reasonable resolution. This provision does not limit rights available under applicable law.',
        },
    ]),
    createSection('section-10', 'Cancellation, ending a trial, suspension and termination', [
        {
            type: 'paragraph',
            text: '10.1 Cancelling a paid subscription: you may cancel by emailing support@baywhiz.com. Cancellation takes effect at the end of the current paid month. You keep access until then, and BayWhiz will not issue further invoices. Section 11.6 explains export and deletion after cancellation.',
        },
        {
            type: 'paragraph',
            text: '10.2 Ending a free trial or free carrier account. Either party may end a free trial at any time by email notice to the other. A carrier may close its free carrier account at any time by emailing support@baywhiz.com, and BayWhiz may close a free carrier account at any time by email notice. Ending a trial or carrier account does not affect the export and deletion terms in Section 11.6 or the Shared Carrier Records terms in Section 2.3.',
        },
        {
            type: 'paragraph',
            text: "10.3 BayWhiz termination of a paid subscription for convenience. BayWhiz may end a paid subscription for convenience on at least thirty (30) calendar days' notice by email, and will refund any prepaid fees for the period after the termination takes effect.",
        },
        {
            type: 'paragraph',
            text: '10.4 Suspension. BayWhiz may decide to suspend access, with notice where practicable, if (a) a renewal invoice remains unpaid after the 10-calendar-day grace period in Section 9.4; (b) your use breaches Section 4 or threatens the security or integrity of the Service; or (c) we are required to do so by law. Where reasonably necessary to address a security threat, suspected unauthorized access or misuse under (b) or (c), BayWhiz may suspend access immediately and will notify you promptly afterwards. Any suspension is applied manually by BayWhiz. We will restore access promptly once the issue is resolved (for example, once the overdue invoice is paid).',
        },
        {
            type: 'paragraph',
            text: "10.5 Termination for breach. Either party may terminate these Terms if the other materially breaches them and does not cure the breach within thirty (30) calendar days of written notice. BayWhiz may terminate immediately by notice for a material breach of Section 4 that threatens the security or integrity of the Service or other customers' data.",
        },
        {
            type: 'paragraph',
            text: '10.6 Sections that by their nature should survive termination (including Sections 11–13 and 17–19, and any unpaid fees) survive.',
        },
    ]),
    createSection('section-11', 'Customer data and processing', [
        {
            type: 'paragraph',
            text: '11.1 Your data. "Customer Data" means the data you and your users submit to the Service for your facilities, and the warehouse-specific records created in your account, including appointments and appointment requests made with you (including details carriers submit when booking with you), check-in, check-out and receiving records, purchase orders and other operational records. As between you and BayWhiz, you own Customer Data. Customer Data does not include Shared Carrier Records (Section 2.3), which BayWhiz maintains for use across the Service, or BayWhiz\'s own account, security, log, analytics and billing records, which are handled under our Privacy Policy.',
        },
        {
            type: 'paragraph',
            text: '11.2 Our permission to process. You grant BayWhiz a limited right to host, copy, process, transmit and display Customer Data only as needed to provide, secure, support and maintain the Service for you, to improve its functionality for you, and as required by law, subject to Section 11.5.',
        },
        {
            type: 'paragraph',
            text: '11.3 Service providers and location. BayWhiz uses service providers to operate the Service, including Google Cloud Platform (hosting, database, backups and logging; United States, us-central1) and Postmark (transactional email). The Service does not currently use a payment processor. If a paid subscription is arranged, BayWhiz will identify any invoicing or payment provider before it is used (Section 8.3). Customer Data is stored and processed in the United States and may be accessed under the laws of that jurisdiction. Our Privacy Policy lists our service providers and describes our use of browser storage.',
        },
        {
            type: 'paragraph',
            text: '11.4 Personal information. Each party will comply with privacy laws that apply to it. For Customer Data, BayWhiz processes personal information on your behalf and under your instructions.',
        },
        {
            type: 'paragraph',
            text: "11.5 Restrictions on use of Customer Data; no aggregated or cross-customer use. BayWhiz will use Customer Data only to: (a) provide, maintain and secure the Service for you, including the scheduling tools, analytics, carrier reliability scorecards and other features you use within your own account; (b) improve the Service's functionality for you; (c) perform support and operational activities that you authorize or that are needed to operate the Service for you (for example, troubleshooting, security monitoring, audit logging and incident investigation); (d) comply with applicable law; and (e) allow its service providers to process Customer Data as necessary to deliver the Service (Section 11.3). BayWhiz will not: (i) sell identifiable Customer Data; (ii) use your identifiable Customer Data to benchmark or profile another customer; (iii) share your identifiable Customer Data with other, unrelated customers; (iv) use Customer Data for advertising or marketing, including BayWhiz's own marketing; (v) use Customer Data to train artificial-intelligence or machine-learning models; or (vi) use Customer Data, including in aggregated or de-identified form, for cross-customer analytics, benchmarking or commercialization unrelated to providing the Service to you. This Section does not apply to Shared Carrier Records (Section 2.3) or to BayWhiz's own account, security, log, analytics and billing records (Section 11.1), which are handled under our Privacy Policy.",
        },
        {
            type: 'paragraph',
            text: '11.6 Export and deletion.',
        },
        {
            type: 'paragraph',
            text: "(a) Available now. While your trial or subscription is active, you can use the Service's existing appointment history and schedule CSV export. Automated export of all Customer Data, and automated deletion of a complete account, are not currently available.",
        },
        {
            type: 'paragraph',
            text: '(b) Export on request. Your account administrator may ask BayWhiz, by email to support@baywhiz.com, to export other available Customer Data, during your trial or subscription or after cancellation or trial expiry. BayWhiz will prepare the export manually, within a reasonable time and in a format BayWhiz reasonably determines (for example, CSV). You should make any request promptly, and if possible within 30 days after cancellation takes effect or the trial expires, because Customer Data may afterwards be deleted or de-identified under (c).',
        },
        {
            type: 'paragraph',
            text: '(c) Deletion. After cancellation takes effect or a trial expires without a paid subscription, or at your verified request, BayWhiz will take reasonable steps to delete or de-identify Customer Data when it is no longer needed to provide the Service, manually and within a reasonable time.',
        },
        {
            type: 'paragraph',
            text: "(d) Limits and exceptions. Export and deletion are subject to applicable law, technical feasibility, verification of the requester's authority, and these exceptions: (i) copies in database backups remain until they are overwritten on the backup provider's rolling cycle; (ii) logs and audit records are kept under the retention practices described in our Privacy Policy, and some application audit records currently have no automatic deletion schedule; (iii) Shared Carrier Records that other customers rely on are not deleted when your Customer Data is deleted, although your own records about them are; and (iv) BayWhiz may keep records it must or may keep for billing, tax, accounting, security or legal purposes, or to establish or defend legal claims.",
        },
        {
            type: 'paragraph',
            text: '(e) No guaranteed timeframe. Any timeframes BayWhiz may describe for export or deletion are operational targets and not guarantees, except where applicable law requires otherwise.',
        },
        {
            type: 'paragraph',
            text: '11.7 Security. BayWhiz maintains reasonable security measures for the Service, which currently include bcrypt password hashing, authentication-token (JWT) validation with database-backed token revocation, tenant isolation between customers, and operational audit logging. No method of transmission or storage is completely secure, and BayWhiz does not guarantee that the Service will be free of security incidents. BayWhiz does not claim any third-party security certification.',
        },
        {
            type: 'paragraph',
            text: '11.8 Security incidents. If BayWhiz becomes aware of a security incident that has resulted, or that BayWhiz reasonably believes has resulted, in unauthorized access to, or disclosure or loss of, your Customer Data, BayWhiz will notify you without undue delay, and in any event as required by applicable law. The notice will describe what BayWhiz then knows about the incident and the steps it is taking. BayWhiz will update you as more information becomes available and give you reasonable information and assistance, including to help you meet any notification obligations you have. Giving notice is not an admission of fault or liability.',
        },
    ]),
    createSection('section-12', 'Confidentiality', [
        {
            type: 'paragraph',
            text: '12.1 Each party will keep the other party\'s non-public business information it receives through the Service or these Terms ("Confidential Information") confidential. It will use that information only to perform these Terms, and will protect it with at least reasonable care.',
        },
        {
            type: 'paragraph',
            text: '12.2 Confidential Information does not include information that is public through no fault of the receiving party, was already known to it, is independently developed, or is lawfully received from a third party without restriction.',
        },
        {
            type: 'paragraph',
            text: '12.3 A party may disclose Confidential Information where required by law, giving notice to the other party where lawful and practicable.',
        },
    ]),
    createSection('section-13', 'Intellectual property', [
        {
            type: 'paragraph',
            text: '13.1 BayWhiz and its licensors own the Service, including its software, design, rules, documentation and trademarks. Except for the limited right to use the Service under these Terms, no rights are granted to you.',
        },
        {
            type: 'paragraph',
            text: '13.2 If you send suggestions or feedback, BayWhiz may use them without obligation to you.',
        },
    ]),
    createSection('section-14', 'Availability and service limitations', [
        {
            type: 'paragraph',
            text: '14.1 We will use commercially reasonable efforts to keep the Service available. However, the Service may be interrupted for maintenance, updates, provider outages or events outside our control. No service-level commitment or service credits apply unless agreed in writing.',
        },
        {
            type: 'paragraph',
            text: "14.2 You should keep your own records of business-critical information and arrange backup procedures (for example, phone or paper processes) for dock operations if the Service is unavailable. BayWhiz's database backups are kept for BayWhiz's own internal disaster recovery and are not a records-retention or archiving service for you. This does not limit BayWhiz's own obligations under applicable privacy and data-protection law, or under Section 11, to protect Customer Data.",
        },
        {
            type: 'paragraph',
            text: '14.3 The Service depends on internet connectivity and third-party infrastructure that BayWhiz does not control.',
        },
    ]),
    createSection('section-15', 'Scheduling, analytics and recommendations', [
        {
            type: 'paragraph',
            text: "15.1 The Service's scheduling tools, automated approval rules, dwell and detention analytics, carrier reliability scorecards, receiving exception analysis, historical delay-risk analysis, recommendations and Copilot use rules, statistical heuristics and deterministic calculations applied to the data in your account. They do not use generative AI or trained predictive machine-learning models.",
        },
        {
            type: 'paragraph',
            text: '15.2 Their outputs depend on the completeness and accuracy of the data entered and the rules you configure. Outputs are informational aids and not guarantees. For example, a delay-risk indication does not mean a delay will or will not occur. Automated approvals follow the rules you set.',
        },
        {
            type: 'paragraph',
            text: "15.3 Dwell and detention figures and scorecards are not a determination of any party's legal or contractual liability, including for detention or accessorial charges between a warehouse and a carrier.",
        },
        {
            type: 'paragraph',
            text: '15.4 You remain responsible for reviewing outputs and for your operational, staffing, safety and commercial decisions.',
        },
    ]),
    createSection('section-16', 'Third-party services', [
        {
            type: 'paragraph',
            text: '16.1 The Service relies on third-party providers (for example, cloud hosting and email delivery and, if a paid subscription is arranged, invoicing and payment processing). Their services are governed by their own terms. BayWhiz is not responsible for third-party services it does not control. BayWhiz remains responsible for its own obligations under these Terms.',
        },
        {
            type: 'paragraph',
            text: '16.2 If you choose to connect, import from or export to third-party systems (for example, CSV or Excel files from your ERP), you are responsible for that data and for those systems.',
        },
    ]),
    createSection('section-17', 'Warranty disclaimer', [
        {
            type: 'paragraph',
            text: '17.1 BayWhiz will provide the Service with reasonable skill and care.',
        },
        {
            type: 'paragraph',
            text: '17.2 Except as expressly stated in these Terms, and to the extent permitted by law, the Service (including any trial) is provided "as is" and "as available". BayWhiz disclaims all other warranties and conditions, express or implied, including merchantability, fitness for a particular purpose, and that the Service will be uninterrupted or error-free.',
        },
    ]),
    createSection('section-18', 'Limitation of liability', [
        {
            type: 'paragraph',
            text: '18.1 Excluded damages. To the extent permitted by law, and subject to Section 18.4, neither party is liable for indirect, incidental, special or consequential damages, or for lost profits, revenue or data. This includes demurrage, detention or accessorial charges, spoilage or labour costs arising from scheduling outcomes, even if advised of the possibility.',
        },
        {
            type: 'paragraph',
            text: "18.2 Cap for paid subscriptions. To the extent permitted by law, and subject to Section 18.4, each party's total liability arising out of or relating to these Terms is limited to the fees paid or payable by you to BayWhiz for the Service in the twelve (12) months before the event giving rise to the claim.",
        },
        {
            type: 'paragraph',
            text: "18.3 Cap for free trials and free carrier accounts. To the extent permitted by law, and subject to Section 18.4, for any free trial and any free Carrier Portal use, BayWhiz's total liability arising out of or relating to these Terms is limited to C$100.",
        },
        {
            type: 'paragraph',
            text: "18.4 Exceptions. The limits in Sections 18.1–18.3 do not apply to: (a) a party's fraud or fraudulent misrepresentation; (b) a party's wilful misconduct; (c) your obligation to pay fees that are due; (d) your breach of Section 4 or infringement of BayWhiz's intellectual property; or (e) any liability that cannot be limited or excluded under applicable law.",
        },
    ]),
    createSection('section-19', 'Governing law and disputes', [
        {
            type: 'paragraph',
            text: '19.1 These Terms are governed by the laws of British Columbia and the federal laws of Canada applicable there.',
        },
        {
            type: 'paragraph',
            text: '19.2 The parties will first try to resolve disputes through good-faith discussion. Subject to that, the courts of British Columbia have jurisdiction.',
        },
    ]),
    createSection('section-20', 'Notices and contact', [
        {
            type: 'paragraph',
            text: '20.1 To BayWhiz: by email to support@baywhiz.com. Please put "Legal Notice" in the subject line of any formal legal notice.',
        },
        {
            type: 'paragraph',
            text: '20.2 To you: to the email address of your account administrator or billing contact, or through notice in the Service.',
        },
        {
            type: 'paragraph',
            text: '20.3 Email notices are received when sent, unless the sender receives a delivery failure.',
        },
    ]),
    createSection('section-21', 'General', [
        {
            type: 'paragraph',
            text: "21.1 Changes to these Terms. We may update these Terms. For material changes, we will give at least thirty (30) calendar days' advance notice by email or in the Service before the change takes effect. Material changes apply from the start of your next monthly billing period that begins after the notice period ends, or, for free accounts and free trials, when you next use the Service after the notice period ends. Where a change is urgently required to comply with law or to address a security risk, we may make it on shorter notice, giving as much notice as is reasonably practicable. Price changes are governed by Section 7.3.",
        },
        {
            type: 'paragraph',
            text: '21.2 Entire agreement. These Terms, and any written paid-subscription arrangement or invoice that refers to them, are the entire agreement. If they conflict, a signed written agreement prevails, then these Terms, then an invoice. Our Privacy Policy explains how we handle personal information. It is a notice and is referred to in these Terms for information.',
        },
        {
            type: 'paragraph',
            text: "21.3 Assignment. Neither party may assign these Terms without the other's consent, except to a successor in a merger, reorganization or sale of substantially all of its relevant business, with notice.",
        },
        {
            type: 'paragraph',
            text: '12.4 Force majeure. Neither party is liable for delays caused by events beyond its reasonable control (payment obligations excepted).'.replace(
                '12.4',
                '21.4'
            ),
        },
        {
            type: 'paragraph',
            text: '21.5 Severability; waiver. If a provision is unenforceable, the rest remains in effect. A failure to enforce is not a waiver.',
        },
        {
            type: 'paragraph',
            text: '21.6 Language. The parties have requested that these Terms be drawn up in English.',
        },
    ]),
];

// Optional runtime override hook used strictly by automated test suites
let testOverrides: LegalGateOverrides | null = null;

export function setLegalGateTestOverrides(overrides: LegalGateOverrides | null): void {
    testOverrides = overrides;
}

export const PRIVACY_POLICY_METADATA: LegalDocumentMetadata = {
    slug: '/privacy',
    title: 'BayWhiz Privacy Policy: Interim Free-Trial Version',
    shortTitle: 'Privacy Policy',
    draftSourceReference: 'N-interim-privacy-policy.pdf (Variant A)',
    version: PUBLISHED_PRIVACY_VERSION,
    approvalStatus: 'approved',
    effectiveDate: resolvePublicationEffectiveDate(),
    lastUpdated: null,
    missingSourceMaterial: [],
    sections: PRIVACY_SECTIONS_V1,
};

export const TERMS_OF_SERVICE_METADATA: LegalDocumentMetadata = {
    slug: '/terms',
    title: 'BayWhiz Terms of Service: Interim Free-Trial Version',
    shortTitle: 'Terms of Service',
    draftSourceReference: 'M-interim-terms-of-service.pdf',
    version: PUBLISHED_TERMS_VERSION,
    approvalStatus: 'approved',
    effectiveDate: resolvePublicationEffectiveDate(),
    lastUpdated: null,
    preambleParagraphs: TERMS_PREAMBLE_V1,
    missingSourceMaterial: [],
    sections: TERMS_SECTIONS_V1,
};

/**
 * Immutable archive of published legal document versions (Q §2: "Each ID refers to
 * exactly one immutable text. Keep an archived copy of every published version").
 */
export const PUBLISHED_LEGAL_VERSION_HISTORY: Readonly<Record<string, LegalDocumentMetadata>> = Object.freeze({
    [PUBLISHED_PRIVACY_VERSION]: PRIVACY_POLICY_METADATA,
    [PUBLISHED_TERMS_VERSION]: TERMS_OF_SERVICE_METADATA,
});

export function getPrivacyPolicyMetadata(): LegalDocumentMetadata {
    const base: LegalDocumentMetadata = {
        ...PRIVACY_POLICY_METADATA,
        effectiveDate: resolvePublicationEffectiveDate(),
    };
    if (!testOverrides?.privacyDocument) return base;
    return {
        ...base,
        ...testOverrides.privacyDocument,
    };
}

export function getTermsOfServiceMetadata(): LegalDocumentMetadata {
    const base: LegalDocumentMetadata = {
        ...TERMS_OF_SERVICE_METADATA,
        effectiveDate: resolvePublicationEffectiveDate(),
    };
    if (!testOverrides?.termsDocument) return base;
    return {
        ...base,
        ...testOverrides.termsDocument,
    };
}

export function getPublishedTermsVersion(): string | null {
    if (testOverrides?.publishedTermsVersion !== undefined) {
        return testOverrides.publishedTermsVersion;
    }
    const winCfg = getRuntimeWindowLegalConfig();
    if (winCfg?.publishedTermsVersion) {
        return winCfg.publishedTermsVersion;
    }
    return PUBLISHED_TERMS_VERSION;
}

export function getPublishedPrivacyVersion(): string | null {
    if (testOverrides?.publishedPrivacyVersion !== undefined) {
        return testOverrides.publishedPrivacyVersion;
    }
    const winCfg = getRuntimeWindowLegalConfig();
    if (winCfg?.publishedPrivacyVersion) {
        return winCfg.publishedPrivacyVersion;
    }
    return PUBLISHED_PRIVACY_VERSION;
}

function isDocumentApprovedAndComplete(doc: LegalDocumentMetadata): boolean {
    return (
        doc.approvalStatus === 'approved' &&
        Boolean(doc.version && doc.version.trim()) &&
        Boolean(doc.effectiveDate && doc.effectiveDate.trim()) &&
        Array.isArray(doc.sections) &&
        doc.sections.length > 0 &&
        Array.isArray(doc.missingSourceMaterial) &&
        doc.missingSourceMaterial.length === 0
    );
}

/**
 * Validates that frontend legal flags and document metadata are consistent.
 */
export function validateFrontendLegalGateConsistency(): { consistent: boolean; errors: string[] } {
    const winCfg = getRuntimeWindowLegalConfig();
    const rawDocsPublished =
        testOverrides?.legalDocsPublished !== undefined
            ? testOverrides.legalDocsPublished
            : winCfg?.legalDocsPublished !== undefined
              ? winCfg.legalDocsPublished
              : LEGAL_DOCS_PUBLISHED;
    const rawAcceptanceRequired =
        testOverrides?.legalAcceptanceRequired !== undefined
            ? testOverrides.legalAcceptanceRequired
            : winCfg?.legalAcceptanceRequired !== undefined
              ? winCfg.legalAcceptanceRequired
              : LEGAL_ACCEPTANCE_REQUIRED;
    const privacyDoc = getPrivacyPolicyMetadata();
    const termsDoc = getTermsOfServiceMetadata();
    const termsVersion = getPublishedTermsVersion();
    const privacyVersion = getPublishedPrivacyVersion();
    const errors: string[] = [];

    if (rawDocsPublished) {
        if (!isDocumentApprovedAndComplete(privacyDoc)) {
            errors.push('Privacy Policy metadata is not approved/complete while legalDocsPublished is true.');
        }
        if (!isDocumentApprovedAndComplete(termsDoc)) {
            errors.push('Terms of Service metadata is not approved/complete while legalDocsPublished is true.');
        }
    }

    if (rawAcceptanceRequired) {
        if (!rawDocsPublished) {
            errors.push('legalAcceptanceRequired cannot be true when legalDocsPublished is false.');
        }
        if (!termsVersion || termsVersion !== termsDoc.version) {
            errors.push('publishedTermsVersion must be non-empty and match Terms of Service metadata version.');
        }
        if (!privacyVersion || privacyVersion !== privacyDoc.version) {
            errors.push('publishedPrivacyVersion must be non-empty and match Privacy Policy metadata version.');
        }
        if (!isDocumentApprovedAndComplete(privacyDoc) || !isDocumentApprovedAndComplete(termsDoc)) {
            errors.push('legalAcceptanceRequired cannot be true unless both legal documents are approved and complete.');
        }
    }

    return {
        consistent: errors.length === 0,
        errors,
    };
}

export function isLegalDocsPublished(): boolean {
    const winCfg = getRuntimeWindowLegalConfig();
    const rawDocsPublished =
        testOverrides?.legalDocsPublished !== undefined
            ? testOverrides.legalDocsPublished
            : winCfg?.legalDocsPublished !== undefined
              ? winCfg.legalDocsPublished
              : LEGAL_DOCS_PUBLISHED;
    if (!rawDocsPublished) return false;
    return (
        isDocumentApprovedAndComplete(getPrivacyPolicyMetadata()) &&
        isDocumentApprovedAndComplete(getTermsOfServiceMetadata())
    );
}

export function isLegalAcceptanceRequired(): boolean {
    const winCfg = getRuntimeWindowLegalConfig();
    const rawAcceptanceRequired =
        testOverrides?.legalAcceptanceRequired !== undefined
            ? testOverrides.legalAcceptanceRequired
            : winCfg?.legalAcceptanceRequired !== undefined
              ? winCfg.legalAcceptanceRequired
              : LEGAL_ACCEPTANCE_REQUIRED;
    if (!rawAcceptanceRequired) return false;
    if (!isLegalDocsPublished()) return false;
    const termsVersion = getPublishedTermsVersion();
    const privacyVersion = getPublishedPrivacyVersion();
    return (
        Boolean(termsVersion && termsVersion === getTermsOfServiceMetadata().version) &&
        Boolean(privacyVersion && privacyVersion === getPrivacyPolicyMetadata().version)
    );
}
